import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { patch, post } from "../../lib/request"
import {
  mapCompetitorDraftToCreateEntityPayload,
  mapCompetitorDraftToUpdateEntityPayload,
  validateCreateEntityPayload,
} from "../../lib/utils/entityCreateMappers"

/** POST /api/v1/entities */
export const createEntity = createAsyncThunk(
  "entityCreate/create",
  async ({ draft, clientId }, { rejectWithValue, getState }) => {
    try {
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []
      const payload = mapCompetitorDraftToCreateEntityPayload(draft, clientId, socialNetworkTypes)
      const validation = validateCreateEntityPayload(payload)
      if (!validation.valid) {
        return rejectWithValue(validation.errors)
      }

      const response = await post("entities", payload)
      return { response, draft }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to create competitor")
    }
  },
)

/** PATCH /api/v1/entities/{id} */
export const updateEntity = createAsyncThunk(
  "entityCreate/update",
  async ({ entityId, draft, clientId }, { rejectWithValue }) => {
    const id = Number(entityId)
    if (!Number.isFinite(id) || id <= 0) {
      return rejectWithValue("Missing entity id")
    }
    try {
      const payload = mapCompetitorDraftToUpdateEntityPayload(draft, clientId)
      const response = await patch(`entities/${id}`, payload)
      return { response, entityId: id, draft }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update competitor")
    }
  },
)

const initialState = {
  loading: false,
  updating: false,
  error: null,
  fieldErrors: null,
  lastCreated: null,
}

const entityCreateSlice = createSlice({
  name: "entityCreate",
  initialState,
  reducers: {
    clearEntityCreate: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEntity.pending, (state) => {
        state.loading = true
        state.error = null
        state.fieldErrors = null
      })
      .addCase(createEntity.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.fieldErrors = null
        state.lastCreated = action.payload?.response ?? null
      })
      .addCase(createEntity.rejected, (state, action) => {
        state.loading = false
        const payload = action.payload
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
          state.fieldErrors = payload
          state.error = Object.values(payload).find(Boolean) || "Failed to create competitor"
        } else {
          state.fieldErrors = null
          state.error = String(payload || "Failed to create competitor")
        }
        state.lastCreated = null
      })
      .addCase(updateEntity.pending, (state) => {
        state.updating = true
        state.error = null
        state.fieldErrors = null
      })
      .addCase(updateEntity.fulfilled, (state) => {
        state.updating = false
        state.error = null
        state.fieldErrors = null
      })
      .addCase(updateEntity.rejected, (state, action) => {
        state.updating = false
        state.error = String(action.payload || "Failed to update competitor")
      })
  },
})

export const { clearEntityCreate } = entityCreateSlice.actions
export default entityCreateSlice.reducer
